import React, { SyntheticEvent, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CompareArrowsOutlinedIcon from "@mui/icons-material/CompareArrowsOutlined";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import PeopleOutlineOutlinedIcon from "@mui/icons-material/PeopleOutlineOutlined";
import { ConfirmDialog, DataTable, EmptyState, PageHeader, useNotify, type DataTableColumn } from "@routes-plan/ui";
import { Office } from "../../../models/office";
import { Route } from "../../../models/route";
import { Loan } from "../../../models/loan";
import { useSelection } from "../../../components/context/SelectionContext";
import HeaderResponsive from "../../../components/layouts/HeaderResponsive";
import { nowOperationalDateString } from "../../../utils/operationalDateTime";
import PermissionButton from "../../../components/common/PermissionButton";
import { useResourcePermissions } from "../../../hooks/useResourcePermissions";
import { formatCurrency } from "../../../utils/format/currency";
import { useT } from "../../../i18n";
import { setPageTitle } from "../../../utils/pageTitle";
import { OfficeRouteFields, Section, SelectField } from "../components/ProcessFilters";
import { selectionColumn } from "../components/selectionColumn";

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const LoanTransfers: React.FC<ShowAlerts> = ({ showLoading, hideLoading }) => {
  const t = useT();
  const notify = useNotify();
  const showAlert = (message: string, severity: "success" | "info" | "warning" | "error" = "info") => notify[severity](message);
  useEffect(() => {
    setPageTitle("loanTransfers.title");
  }, []);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { canTransfer } = useResourcePermissions('/processes/loan-transfers');
  const formattedDate = nowOperationalDateString();
  const {
    office_id: officeId,
    setOffice_id: setOfficeId,
    collect_id: routeId,
    setCollect_id: setRouteId,
    user_id: userId,
    setUser_id: setUserId,
  } = useSelection();

  const [officeRoutes, setOfficeRoutes] = useState<Route[]>([]);
  const [routeLoans, setRouteLoans] = useState<Loan[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [targetOfficeId, setTargetOfficeId] = useState("");
  const [targetOfficeRoutes, setTargetOfficeRoutes] = useState<Route[]>([]);
  const [targetRouteId, setTargetRouteId] = useState("");
  const [selectedLoans, setSelectedLoans] = useState<(string | number)[]>([]);

  const orderedRouteLoans = useMemo(() => {
    return [...routeLoans].sort((a, b) => {
      const pa = a?.position != null ? Number(a.position) : Number.POSITIVE_INFINITY;
      const pb = b?.position != null ? Number(b.position) : Number.POSITIVE_INFINITY;
      if (pa !== pb) {
        return pa - pb;
      }
      return Number(a?.id ?? 0) - Number(b?.id ?? 0);
    });
  }, [routeLoans]);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: userResp }, { data: officesResp }] = await Promise.all([
          axios.get("user"),
          axios.get("offices"),
        ]);
        setUserId(String(userResp?.data?.id ?? ""));
        setOffices(Array.isArray(officesResp?.data) ? officesResp.data : []);
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
    })();
  }, [setUserId]);

  useEffect(() => {
    if (!officeId) {
      setOfficeRoutes([]);
      return;
    }

    (async () => {
      try {
        const { data } = await axios.get(`offices/${officeId}/routes`);
        setOfficeRoutes(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching routes:", error);
      }
    })();
  }, [officeId]);

  useEffect(() => {
    setTargetRouteId("");

    if (!targetOfficeId) {
      setTargetOfficeRoutes([]);
      return;
    }

    (async () => {
      try {
        const { data } = await axios.get(`offices/${targetOfficeId}/routes`);
        setTargetOfficeRoutes(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching target routes:", error);
      }
    })();
  }, [targetOfficeId]);

  useEffect(() => {
    if (!routeId) {
      setRouteLoans([]);
      setSelectedLoans([]);
      return;
    }

    (async () => {
      try {
        const { data } = await axios.get(`routes/${routeId}/loans`);
        setRouteLoans(Array.isArray(data?.data) ? data.data : []);
      } catch (error) {
        console.error("Error fetching loans:", error);
      }
    })();
  }, [routeId]);

  // Valida y pide confirmación antes de transferir.
  const askTransfer = () => {
    if (!canTransfer) {
      showAlert(t('loanTransfers.noPermissionAlert'), 'warning');
      return;
    }
    if (!routeId || !targetRouteId) {
      showAlert(t('loanTransfers.routesRequired'), 'warning');
      return;
    }
    if (selectedLoans.length === 0) {
      showAlert(t('loanTransfers.loansRequired'), 'warning');
      return;
    }
    setConfirmOpen(true);
  };

  const submit = async (event?: SyntheticEvent) => {
    event?.preventDefault();
    setConfirmOpen(false);

    if (!canTransfer) {
      showAlert(t('loanTransfers.noPermissionAlert'), 'warning');
      return;
    }

    if (!routeId || !targetRouteId) {
      showAlert(t('loanTransfers.routesRequired'), 'warning');
      return;
    }

    if (selectedLoans.length === 0) {
      showAlert(t('loanTransfers.loansRequired'), 'warning');
      return;
    }

    const selectedLoanIds = selectedLoans.map((id) => String(id));
    const selectedClientIds = selectedLoanIds
      .map((selectedLoanId) => {
        const matchedLoan = orderedRouteLoans.find(
          (loan) => String(loan.id) === selectedLoanId,
        );
        return matchedLoan?.client?.id ? String(matchedLoan.client.id) : "";
      })
      .filter(Boolean);

    const formData = {
      sourceRouteId: routeId,
      date: formattedDate,
      targetRouteId,
      userId,
      clientId: selectedClientIds,
      loanId: selectedLoanIds,
    };

    try {
      showLoading();
      await axios.post("/loan-transfers", formData);
      showAlert(t('loanTransfers.transferred'), 'success');
      setSelectedLoans([]);
      setRouteId("");
      setTargetOfficeId("");
      setTargetRouteId("");
      setRouteLoans([]);
    } catch (error) {
      console.error('Error transferring loan:', error);
      const backendMessage =
        axios.isAxiosError(error)
          ? String(error.response?.data?.error ?? error.response?.data?.message ?? '').trim()
          : '';
      showAlert(backendMessage || t('loanTransfers.transferError'), 'error');
    } finally {
      hideLoading();
    }
  };

  type Row = { id: number; positionOrdinal: number; idNumber: string; fullName: string; phone: string; mainAddress: string; balance: number; routeName: string };
  const rows: Row[] = useMemo(
    () =>
      orderedRouteLoans.map((loan, index) => ({
        id: loan.id,
        positionOrdinal: index + 1,
        idNumber: loan.client?.idNumber ?? '',
        fullName: loan.client?.fullName ?? '—',
        phone: loan.client?.phone ?? '',
        mainAddress: loan.client?.mainAddress ?? '',
        balance: Number(loan.balance ?? 0),
        routeName: loan.route?.name ?? '',
      })),
    [orderedRouteLoans],
  );

  const columns: DataTableColumn<Row>[] = [
    selectionColumn<Row>({
      rows,
      getId: (r) => r.id,
      selected: selectedLoans,
      onChange: setSelectedLoans,
      selectAllLabel: t('loanTransfers.selectAll'),
      selectRowLabel: (r) => t('loanTransfers.selectRow', { name: r.fullName }),
      disabled: !canTransfer,
    }),
    { id: 'positionOrdinal', header: t('loanTransfers.colPosition'), value: (r) => r.positionOrdinal, sortable: true, align: 'center', width: 64 },
    { id: 'fullName', header: t('loanTransfers.colClient'), value: (r) => r.fullName, sortable: true },
    { id: 'idNumber', header: t('common.idNumber'), value: (r) => r.idNumber, sortable: true, hideBelow: 'sm' },
    { id: 'phone', header: t('common.phone'), value: (r) => r.phone, hideBelow: 'md' },
    { id: 'mainAddress', header: t('common.address'), value: (r) => r.mainAddress, wrap: true, hideBelow: 'md' },
    { id: 'balance', header: t('loanTransfers.colBalance'), value: (r) => r.balance, render: (r) => formatCurrency(r.balance), align: 'right', sortable: true },
  ];

  const sourceRouteName = officeRoutes.find((r) => String(r.id) === String(routeId))?.name ?? '';
  const targetRouteName = targetOfficeRoutes.find((r) => String(r.id) === String(targetRouteId))?.name ?? '';

  return (
    <HeaderResponsive>
      <PageHeader
        title={t('loanTransfers.title')}
        description={t('loanTransfers.subtitle')}
        actions={
          <PermissionButton
            allowed={canTransfer}
            deniedTooltip={t('loanTransfers.noPermission')}
            variant="contained"
            onClick={askTransfer}
            disabled={selectedLoans.length === 0 || !targetRouteId}
            startIcon={<CompareArrowsOutlinedIcon />}
          >
            {selectedLoans.length > 0 ? t('loanTransfers.transferCount', { count: selectedLoans.length }) : t('loanTransfers.transfer')}
          </PermissionButton>
        }
      />

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          mb: 2,
          alignItems: 'stretch',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) auto minmax(0, 1fr)' },
        }}
      >
        <Section title={t('loanTransfers.sourceTitle')}>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <OfficeRouteFields
              idPrefix="transfer-source"
              offices={offices}
              routes={officeRoutes}
              officeId={officeId}
              routeId={routeId}
              onOfficeChange={(v) => {
                setOfficeId(v);
                setRouteId('');
              }}
              onRouteChange={(v) => {
                setSelectedLoans([]);
                setRouteId(v);
              }}
            />
          </Box>
        </Section>
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', color: 'text.secondary' }} aria-hidden>
          <ArrowForwardOutlinedIcon />
        </Box>
        <Section title={t('loanTransfers.targetTitle')}>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <SelectField
              id="transfer-target-office"
              label={t('loanTransfers.targetOffice')}
              value={targetOfficeId}
              options={offices}
              onChange={setTargetOfficeId}
            />
            <SelectField
              id="transfer-target-route"
              label={t('loanTransfers.targetRoute')}
              value={targetRouteId}
              options={targetOfficeRoutes.filter((route) => String(route.id) !== String(routeId))}
              onChange={setTargetRouteId}
              disabled={!targetOfficeId}
            />
          </Box>
        </Section>
      </Box>

      <Section
        title={t('loanTransfers.loansTitle')}
        action={
          rows.length > 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('loanTransfers.selectedCount', { count: selectedLoans.length, total: rows.length })}
            </Typography>
          ) : undefined
        }
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t('loanTransfers.hint')}
        </Typography>
        <DataTable<Row>
          rows={rows}
          columns={columns}
          getRowId={(r) => r.id}
          searchable
          exportFileName={t('loanTransfers.exportFileName')}
          initialPageSize={50}
          ariaLabel={t('loanTransfers.loansTitle')}
          stickyFirstColumn
          emptyState={
            <EmptyState
              dense
              icon={<PeopleOutlineOutlinedIcon />}
              title={t('loanTransfers.empty')}
              description={routeId ? undefined : t('loanTransfers.selectToStart')}
            />
          }
        />
      </Section>

      <ConfirmDialog
        open={confirmOpen}
        variant="warning"
        title={t('loanTransfers.confirmTitle')}
        message={t('loanTransfers.confirmMessage', { count: selectedLoans.length, source: sourceRouteName, target: targetRouteName })}
        confirmLabel={t('loanTransfers.transfer')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => void submit()}
        onCancel={() => setConfirmOpen(false)}
      />
    </HeaderResponsive>
  );
};

export default LoanTransfers;
